/**
 * Shared shopper-location state: ZIP, USDA growing zone, and state abbreviation.
 *
 * Ported from the growing-zone picker in sill-dawn's header.liquid, with three changes:
 * the network call uses fetch instead of pulling in axios from a CDN, the state lives in one
 * module instead of being duplicated per mount point, and consumers subscribe to a change event
 * rather than the module reaching out to specific element IDs. That last part is what lets the
 * announcement bar, the drawer and the modal all stay in sync without knowing about each other.
 *
 * Location and zones come from The Sill's Customer Data Hub (Theme settings → Customer Data Hub):
 * `/geo` for the visitor's IP location and `/geo/zone?zip=` for a ZIP the shopper types, so the
 * site and the Data Hub always agree on a zone.
 *
 * localStorage keys are deliberately the same as the live theme — `userZip`, `userZone` and
 * `userRegionCode` — so anything else ported over from sill-dawn keeps working untouched.
 * `userZone` stays the whole-number zone (`zone_number`) every reader expects; the Data Hub's
 * half-zone (`growing_zone`, e.g. "7b") rides alongside in `userGrowingZone`, and
 * `userZipSource` records whether the ZIP was detected ('ip') or typed ('manual').
 */

const ZIP_KEY = 'userZip';
const ZONE_KEY = 'userZone';
const STATE_KEY = 'userRegionCode';
const GROWING_ZONE_KEY = 'userGrowingZone';
const SOURCE_KEY = 'userZipSource';
const LOCATION_CACHE_KEY = 'locationCache';

// A detected location is looked up again after a day, in case the shopper has moved.
const LOCATION_CACHE_DURATION = 24 * 60 * 60 * 1000;

// Zones don't change, so a ZIP's zone is good for a month.
const ZONE_CACHE_DURATION = 30 * 24 * 60 * 60 * 1000;

const CDH_DEFAULT_URL = 'https://cdh-app-fvwrq.ondigitalocean.app';
const CDH_TIMEOUT = 3000;

export const LOCATION_CHANGE_EVENT = 'user-location:change';

/** localStorage throws in private mode and when the quota is full; never let that break a page. */
const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, value == null ? '' : String(value));
  } catch {
    /* nothing we can do, and nothing that should surface to the shopper */
  }
};

const readCache = (key, maxAge) => {
  const raw = read(key);
  if (!raw) return null;

  try {
    const { data, timestamp } = JSON.parse(raw);
    if (!timestamp || Date.now() - timestamp > maxAge) return null;
    return data;
  } catch {
    return null;
  }
};

const writeCache = (key, data) => write(key, JSON.stringify({ data, timestamp: Date.now() }));

/** `Theme` is declared by snippets/scripts.liquid, which also carries the theme setting. */
const cdhUrl = () => {
  const configured = typeof Theme !== 'undefined' ? Theme.cdh?.url : '';
  return String(configured || CDH_DEFAULT_URL).trim().replace(/\/+$/, '');
};

/**
 * GETs a Data Hub endpoint. Resolves null when no answer came back at all — a timeout, network
 * error or ad blocker — and never rejects, so nothing reaches the console.
 * @param {string} path
 * @returns {Promise<{status: number, data: any}|null>}
 */
async function fetchCdh(path) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CDH_TIMEOUT);

  try {
    const response = await fetch(`${cdhUrl()}${path}`, { signal: controller.signal, credentials: 'omit' });
    const data = response.ok ? await response.json() : null;
    return { status: response.status, data };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** The Data Hub returns `zone_number` as a number; it's stored the way `userZone` always was. */
const zoneNumber = (value) => (/^\d+$/.test(String(value ?? '')) ? String(Number(value)) : null);

/**
 * ZIP prefix ranges by state, as USPS allocates them: [first ZIP3, last ZIP3, abbreviation].
 * Enough to name the state a ZIP is in, which is what the shipping restrictions on a product
 * page are keyed on (see assets/shipping-restrictions.js) — a shopper who types a ZIP would
 * otherwise have no state at all, since only the IP lookup reports one.
 *
 * Military (090-098, 962-966) and Pacific territory (969) prefixes are deliberately absent:
 * they aren't states, and nothing restricts shipping by them.
 */
const ZIP_PREFIX_RANGES = [
  [5, 5, 'NY'],
  [6, 9, 'PR'],
  [10, 27, 'MA'],
  [28, 29, 'RI'],
  [30, 38, 'NH'],
  [39, 49, 'ME'],
  [50, 59, 'VT'],
  [60, 69, 'CT'],
  [70, 89, 'NJ'],
  [100, 149, 'NY'],
  [150, 196, 'PA'],
  [197, 199, 'DE'],
  [200, 200, 'DC'],
  [201, 201, 'VA'],
  [202, 205, 'DC'],
  [206, 219, 'MD'],
  [220, 246, 'VA'],
  [247, 268, 'WV'],
  [270, 289, 'NC'],
  [290, 299, 'SC'],
  [300, 319, 'GA'],
  [320, 349, 'FL'],
  [350, 369, 'AL'],
  [370, 385, 'TN'],
  [386, 397, 'MS'],
  [398, 399, 'GA'],
  [400, 427, 'KY'],
  [430, 459, 'OH'],
  [460, 479, 'IN'],
  [480, 499, 'MI'],
  [500, 528, 'IA'],
  [530, 549, 'WI'],
  [550, 567, 'MN'],
  [570, 577, 'SD'],
  [580, 588, 'ND'],
  [590, 599, 'MT'],
  [600, 629, 'IL'],
  [630, 658, 'MO'],
  [660, 679, 'KS'],
  [680, 693, 'NE'],
  [700, 714, 'LA'],
  [716, 729, 'AR'],
  [730, 749, 'OK'],
  [750, 799, 'TX'],
  [800, 816, 'CO'],
  [820, 831, 'WY'],
  [832, 838, 'ID'],
  [840, 847, 'UT'],
  [850, 865, 'AZ'],
  [870, 884, 'NM'],
  [885, 885, 'TX'],
  [889, 898, 'NV'],
  [900, 961, 'CA'],
  [967, 968, 'HI'],
  [970, 979, 'OR'],
  [980, 994, 'WA'],
  [995, 999, 'AK'],
];

/**
 * @param {string} zip
 * @returns {string|null} The state abbreviation, or null for a ZIP outside the ranges above.
 */
export function stateForZip(zip) {
  if (!/^\d{5}$/.test(zip)) return null;

  const prefix = Number(zip.slice(0, 3));
  const range = ZIP_PREFIX_RANGES.find(([first, last]) => prefix >= first && prefix <= last);

  return range ? String(range[2]) : null;
}

/**
 * @returns {{zip: string|null, zone: string|null, state: string|null, growingZone: string|null, source: string|null}}
 */
export const getLocation = () => ({
  zip: read(ZIP_KEY) || null,
  zone: read(ZONE_KEY) || null,
  state: read(STATE_KEY) || null,
  growingZone: read(GROWING_ZONE_KEY) || null,
  source: read(SOURCE_KEY) || null,
});

const announce = () => {
  document.dispatchEvent(new CustomEvent(LOCATION_CHANGE_EVENT, { detail: getLocation() }));
};

/** The ZIP the last IP lookup reported, however old. @returns {string|null} */
const detectedZip = () => {
  try {
    return JSON.parse(read(LOCATION_CACHE_KEY) || 'null')?.data?.postal || null;
  } catch {
    return null;
  }
};

/**
 * The zonemapper asset URL can only be produced by Liquid, so it rides in on whichever mount
 * point rendered first rather than being hardcoded here.
 * @returns {string|null}
 */
const zonemapUrl = () => document.querySelector('[data-zonemap-url]')?.dataset.zonemapUrl ?? null;

/**
 * ZIP -> zone from the theme's own zonemapper.json. Only used when the Data Hub can't be reached,
 * so the modal keeps working through an outage. Not cached, so the Data Hub is asked again next time.
 * @param {string} zip
 * @returns {Promise<{zone: string, growingZone: null}|null>}
 */
async function lookupZonemap(zip) {
  const url = zonemapUrl();
  if (!url) return null;

  try {
    const response = await fetch(url);
    const data = await response.json();
    const zone = zoneNumber(data[zip]?.growing_zone);
    return zone ? { zone, growingZone: null } : null;
  } catch {
    return null;
  }
}

/**
 * ZIP -> growing zone, via the Data Hub's `/geo/zone`. Cached per ZIP under `zoneCache-<zip>`;
 * entries without a `growing_zone` predate the Data Hub and are ignored, so a stale local answer
 * can't disagree with it. A 404 means the ZIP has no zone. A timeout or any other failure falls
 * back to zonemapper.json, whose answer has no half zone.
 * @param {string} zip
 * @returns {Promise<{zone: string, growingZone: string|null}|null>}
 */
async function resolveZone(zip) {
  if (!/^\d{5}$/.test(zip)) return null;

  const cached = readCache(`zoneCache-${zip}`, ZONE_CACHE_DURATION);
  if (cached?.zone != null && cached.growing_zone) {
    return { zone: String(cached.zone), growingZone: String(cached.growing_zone) };
  }

  const response = await fetchCdh(`/geo/zone?zip=${encodeURIComponent(zip)}`);

  if (response?.data) {
    const zone = zoneNumber(response.data.zone_number);
    if (!zone || !response.data.growing_zone) return null;

    const growingZone = String(response.data.growing_zone);
    writeCache(`zoneCache-${zip}`, { zone: Number(zone), growing_zone: growingZone, zone_number: Number(zone) });
    return { zone, growingZone };
  }

  // 400 can't happen for a 5-digit ZIP, but it means the same thing as a 404.
  if (response?.status === 404 || response?.status === 400) return null;

  return lookupZonemap(zip);
}

/**
 * @param {string} zip
 * @returns {Promise<string|null>} The whole-number zone, or null when the ZIP has none.
 */
export async function lookupZone(zip) {
  return (await resolveZone(zip))?.zone ?? null;
}

/**
 * Records a ZIP the shopper typed in.
 *
 * A ZIP that can't be zoned is rejected rather than stored: persisting it would overwrite a
 * good value with one that can never resolve a zone, and because ensureLocation() trusts a stored
 * ZIP it would never fall back to the IP lookup again — one typo and the badge stays blank on
 * every subsequent page. The live theme stores it either way; this is a deliberate divergence.
 *
 * The state abbreviation is re-derived from the ZIP rather than kept, because the stored one came
 * from the IP lookup and a hand-entered ZIP may be somewhere else entirely. Keeping it in step
 * matters on the product page, where a shopper correcting their ZIP is how they get out of a
 * shipping restriction the IP lookup put them in.
 *
 * @param {string} zip
 * @returns {Promise<string|null>} The resolved zone, or null when the ZIP couldn't be zoned.
 */
export async function setZip(zip) {
  const resolved = await resolveZone(zip);
  if (!resolved) return null;

  write(ZIP_KEY, zip);
  write(ZONE_KEY, resolved.zone);
  write(GROWING_ZONE_KEY, resolved.growingZone ?? '');
  write(STATE_KEY, stateForZip(zip) ?? '');
  write(SOURCE_KEY, 'manual');
  announce();

  return resolved.zone;
}

/**
 * Tells Shopify's customer events the shopper settled on a zone. Only the ZIP3 leaves the browser.
 * @param {string} zip
 * @param {string} zone
 * @param {'ip_confirmed'|'manual'} source
 */
function publishZoneSet(zip, zone, source) {
  const analytics = window.Shopify?.analytics;
  if (typeof analytics?.publish !== 'function') return;

  try {
    analytics.publish('cdh.growing_zone_set', {
      zip3: zip.slice(0, 3),
      // The half zone from the Data Hub, or the whole zone when zonemapper.json answered.
      growing_zone: read(GROWING_ZONE_KEY) || String(zone),
      source,
    });
  } catch {
    /* analytics must never break the confirm */
  }
}

/**
 * setZip() for the Confirm buttons in the modal and the growing-zone finder: stores the ZIP and
 * publishes `cdh.growing_zone_set` once. Submitting the ZIP the IP lookup detected counts as
 * accepting the detected zone.
 * @param {string} zip
 * @returns {Promise<string|null>} The resolved zone, or null when the ZIP couldn't be zoned.
 */
export async function confirmZip(zip) {
  const source = zip === detectedZip() ? 'ip_confirmed' : 'manual';

  const zone = await setZip(zip);
  if (zone) publishZoneSet(zip, zone, source);

  return zone;
}

/**
 * Visitor location from the Data Hub's `/geo`, which answers with IPAPI-style aliases
 * (`postal`, `region_code`) alongside its own zone fields.
 * @returns {Promise<void>}
 */
async function lookupByIp() {
  const cached = readCache(LOCATION_CACHE_KEY, LOCATION_CACHE_DURATION);
  // Caches written by the old IPAPI lookup have no zone fields; look those up afresh.
  if (cached?.postal && 'zone_number' in cached) return applyIpResult(cached);

  const response = await fetchCdh('/geo');
  const data = response?.data;
  // Ad blockers, rate limits and visitors outside the US all land here. The shopper can still
  // enter a ZIP by hand.
  if (!data || !/^\d{5}$/.test(String(data.postal ?? ''))) return;

  const result = {
    postal: String(data.postal),
    region_code: data.region_code || '',
    growing_zone: data.growing_zone || null,
    zone_number: zoneNumber(data.zone_number) ? Number(data.zone_number) : null,
  };

  writeCache(LOCATION_CACHE_KEY, result);
  applyIpResult(result);
}

/**
 * @param {{postal: string, region_code?: string, growing_zone?: string|null, zone_number?: number|null}} result
 */
function applyIpResult({ postal, region_code: regionCode, growing_zone: growingZone, zone_number: number }) {
  // A typed ZIP always wins — including one entered while this lookup was in flight.
  if (read(SOURCE_KEY) === 'manual') return;

  write(ZIP_KEY, postal);
  // The Data Hub usually reports a region, but falls back to the ZIP when it doesn't.
  write(STATE_KEY, regionCode || stateForZip(postal) || '');
  write(ZONE_KEY, zoneNumber(number) ?? '');
  write(GROWING_ZONE_KEY, growingZone ?? '');
  write(SOURCE_KEY, 'ip');

  announce();
}

/**
 * Re-zones a stored ZIP without replacing it.
 * @param {string} zip
 */
async function refreshZone(zip) {
  const resolved = await resolveZone(zip);
  if (resolved) {
    write(ZONE_KEY, resolved.zone);
    write(GROWING_ZONE_KEY, resolved.growingZone ?? '');
  }
  announce();
}

/**
 * Fills in whatever is missing, cheapest path first: a stored ZIP and zone are trusted, a stored
 * ZIP with no zone (or one from before the Data Hub) only needs `/geo/zone`, and `/geo` is only
 * called for an unknown shopper or a detected location more than a day old. A typed ZIP is never
 * replaced by IP detection.
 * @returns {Promise<void>}
 */
export async function ensureLocation() {
  const { zip, zone, state, growingZone } = getLocation();
  let source = read(SOURCE_KEY);

  // Sessions that predate setZip storing a state have a ZIP and no state; fill it in rather than
  // spend an IP lookup, which would only tell us what the ZIP already does.
  if (zip && !state) write(STATE_KEY, stateForZip(zip) ?? '');

  // Sessions from before userZipSource: a ZIP matching the last IP lookup came from it; anything
  // else was typed, and must be protected as such.
  if (zip && !source) {
    source = zip === detectedZip() ? 'ip' : 'manual';
    write(SOURCE_KEY, source);
  }

  if (!zip || (source === 'ip' && !readCache(LOCATION_CACHE_KEY, LOCATION_CACHE_DURATION))) {
    await lookupByIp();
    return;
  }

  // A zone with no half zone beside it came from zonemapper.json; ask the Data Hub again so the
  // badge agrees with it.
  if (zone && growingZone) {
    announce();
    return;
  }

  await refreshZone(zip);
}

/**
 * The badge showing the shopper's zone and ZIP.
 *
 * A custom element rather than a script that paints `[data-zone-badge]` nodes, because the header
 * ships a light first render and fills the drawer in on a second pass — so a drawer badge can
 * appear, or be reset to its server-rendered blank state, long after the location resolved.
 * connectedCallback fires on every one of those, which a one-shot paint at DOMContentLoaded and a
 * MutationObserver watching for additions both miss.
 */
class ZoneBadge extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    this.paint();

    // The header ships a light first render and fills the drawer in on a second pass, which
    // patches this badge's children back to their blank server-rendered state without
    // disconnecting the node — so connectedCallback alone never fires again and the drawer badge
    // sits empty while the announcement one is fine. Watching our own (tiny) subtree repaints
    // whatever resets it, without needing to know which pass did it. paint() is a no-op when the
    // markup already matches, so this can't loop.
    this.#observer = new MutationObserver(() => this.paint());
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  paint() {
    const { zip, zone } = getLocation();
    const hasBoth = Boolean(zip && zone);

    const known = this.querySelector('[data-zone-badge-known]');
    const prompt = this.querySelector('[data-zone-badge-prompt]');
    const zoneOut = this.querySelector('[data-zone-badge-zone]');
    const zipOut = this.querySelector('[data-zone-badge-zip]');

    const zoneText = zone ?? '';
    const zipText = zip ?? '';

    // Only write when something actually differs, so the observer above settles immediately.
    if (zoneOut && zoneOut.textContent !== zoneText) zoneOut.textContent = zoneText;
    if (zipOut && zipOut.textContent !== zipText) zipOut.textContent = zipText;
    if (known && known.hidden !== !hasBoth) known.hidden = !hasBoth;
    if (prompt && prompt.hidden !== hasBoth) prompt.hidden = hasBoth;
  }
}

if (!customElements.get('zone-badge')) {
  customElements.define('zone-badge', ZoneBadge);
}

const paintBadges = () => {
  for (const badge of document.querySelectorAll('zone-badge')) {
    if (badge instanceof ZoneBadge) badge.paint();
  }
};

document.addEventListener(LOCATION_CHANGE_EVENT, paintBadges);

// Resolve once per page load. Badges paint from storage on connect, so a returning shopper never
// sees the placeholder flash while the network call is still in flight.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ensureLocation, { once: true });
} else {
  ensureLocation();
}

// Exposed for the modal's inline script, which isn't a module.
window.SillLocation = { getLocation, setZip, confirmZip, lookupZone, ensureLocation, stateForZip, LOCATION_CHANGE_EVENT };
